/**
 * AssociationTargetEditor — lets the user change WHAT a created entity is
 * associated with, by searching for a different existing CRM entity.
 *
 * Used in MergedCreateActionCard's edit mode: each mergeable association whose
 * target is a searchable entity type (company / contact / deal / user) renders
 * one of these; on change it reports the newly-picked entity up so the merged
 * card can persist the new target_entity_id on save. Associations whose target
 * type isn't searchable stay read-only (the caller renders AssociationEntityCard
 * for those instead).
 */

import { useCallback } from 'react';

import { EntityTypeahead } from '@/lib/crm/components/EntityTypeahead';
import type { EntityResult } from '@/lib/search';

import type { MergedAssociation } from '../../lib/proposals/utils/actionMerging';
import {
  getSearchableTargetType,
  TARGET_TYPE_NOUN,
  TARGET_TYPE_PLURAL,
} from './associationTargets';

interface AssociationTargetEditorProps {
  assoc: MergedAssociation;
  orgId: number;
  value: EntityResult | null;
  onChange: (value: EntityResult | null) => void;
  /** Entities being created in this proposal that can also be picked here. */
  extraOptions?: EntityResult[];
}

export function AssociationTargetEditor({
  assoc,
  orgId,
  value,
  onChange,
  extraOptions,
}: AssociationTargetEditorProps) {
  const handleChange = useCallback(
    (next: EntityResult | EntityResult[] | null) =>
      onChange(Array.isArray(next) ? (next[0] ?? null) : next),
    [onChange]
  );

  const type = getSearchableTargetType(assoc);
  // Caller only renders this for searchable associations, but guard for safety.
  if (!type) {
    return null;
  }

  const noun = TARGET_TYPE_NOUN[type] ?? 'record';
  const plural = TARGET_TYPE_PLURAL[type] ?? 'records';

  return (
    <div className='flex flex-col gap-1.5'>
      <label className='text-xs font-medium text-fg-tertiary'>
        Associated {noun}
      </label>
      <EntityTypeahead
        orgId={orgId}
        types={[type]}
        value={value ?? undefined}
        onChange={handleChange}
        placeholder={`Search ${plural}`}
        extraOptions={extraOptions}
      />
    </div>
  );
}
